import { formatSignupOptionSchedule, localDateToMs } from './dates';
import type { ApplicationWithDetails, EventSignupOption } from '../types/database';

export const NO_CHURCH = 'No Church Specified';
export const NO_OPTION = 'No Option Selected';

export interface ApplicationsReportFilters {
  status: string;
  searchQuery: string;
  groupByChurch: boolean;
  groupByOption: boolean;
}

export interface ChurchApplicationGroup {
  churchName: string;
  applications: ApplicationWithDetails[];
}

export interface OptionApplicationGroup {
  optionId: string | null;
  optionLabel: string;
  applications: ApplicationWithDetails[];
  churchGroups?: ChurchApplicationGroup[];
}

export function sortChurchNames(churches: string[]): string[] {
  return churches.sort((a, b) => {
    if (a === NO_CHURCH) return 1;
    if (b === NO_CHURCH) return -1;
    return a.localeCompare(b);
  });
}

export function groupApplicationsByChurch(
  applications: ApplicationWithDetails[]
): ChurchApplicationGroup[] {
  const groups: Record<string, ApplicationWithDetails[]> = {};

  applications.forEach((app) => {
    const churchName = app.attendee_profile?.church_info?.church_name || NO_CHURCH;
    if (!groups[churchName]) {
      groups[churchName] = [];
    }
    groups[churchName].push(app);
  });

  return sortChurchNames(Object.keys(groups)).map((churchName) => ({
    churchName,
    applications: groups[churchName],
  }));
}

export function sortSignupOptions(options: EventSignupOption[]): EventSignupOption[] {
  return [...options].sort((a, b) => {
    const aDate = a.start_date ? localDateToMs(a.start_date) : Number.POSITIVE_INFINITY;
    const bDate = b.start_date ? localDateToMs(b.start_date) : Number.POSITIVE_INFINITY;
    if (aDate !== bDate) return aDate - bDate;
    if (a.display_order !== b.display_order) return a.display_order - b.display_order;
    return a.label.localeCompare(b.label);
  });
}

export function formatOptionLabel(option: EventSignupOption): string {
  const schedule = formatSignupOptionSchedule(option);
  return schedule ? `${option.label} (${schedule})` : option.label;
}

export function getSelectedOptionsForApp(
  app: ApplicationWithDetails,
  optionById: Map<string, EventSignupOption>
): EventSignupOption[] {
  return (app.selected_signup_options || [])
    .map((id) => optionById.get(id))
    .filter((option): option is EventSignupOption => Boolean(option));
}

export function filterApplications(
  applications: ApplicationWithDetails[],
  { status, searchQuery }: Pick<ApplicationsReportFilters, 'status' | 'searchQuery'>
): ApplicationWithDetails[] {
  let filtered = [...applications];

  if (status !== 'all') {
    filtered = filtered.filter((app) => app.status === status);
  }

  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter((app) => {
      const profile = app.attendee_profile;
      return (
        profile?.first_name?.toLowerCase().includes(query) ||
        profile?.last_name?.toLowerCase().includes(query) ||
        profile?.email?.toLowerCase().includes(query) ||
        profile?.cell_phone?.includes(query)
      );
    });
  }

  return filtered;
}

export function buildGroupedByChurch(
  filteredApplications: ApplicationWithDetails[],
  groupByChurch: boolean,
  groupByOption: boolean
): ChurchApplicationGroup[] | null {
  if (!groupByChurch || groupByOption) return null;
  return groupApplicationsByChurch(filteredApplications);
}

export function buildGroupedByOption(
  filteredApplications: ApplicationWithDetails[],
  signupOptions: EventSignupOption[],
  groupByOption: boolean,
  groupByChurch: boolean
): OptionApplicationGroup[] | null {
  if (!groupByOption || signupOptions.length === 0) return null;

  const sortedOptions = sortSignupOptions(signupOptions);
  const assignedAppIds = new Set<string>();
  const groups: OptionApplicationGroup[] = [];

  for (const option of sortedOptions) {
    const applications = filteredApplications.filter((app) =>
      (app.selected_signup_options || []).includes(option.id)
    );
    applications.forEach((app) => assignedAppIds.add(app.id));
    if (applications.length === 0) continue;

    groups.push({
      optionId: option.id,
      optionLabel: formatOptionLabel(option),
      applications,
      churchGroups: groupByChurch ? groupApplicationsByChurch(applications) : undefined,
    });
  }

  const unassignedApplications = filteredApplications.filter((app) => !assignedAppIds.has(app.id));
  if (unassignedApplications.length > 0) {
    groups.push({
      optionId: null,
      optionLabel: NO_OPTION,
      applications: unassignedApplications,
      churchGroups: groupByChurch
        ? groupApplicationsByChurch(unassignedApplications)
        : undefined,
    });
  }

  return groups;
}

export function buildApplicationsPrintUrl(
  eventId: string,
  filters: ApplicationsReportFilters
): string {
  const params = new URLSearchParams();
  if (filters.status !== 'all') params.set('status', filters.status);
  if (filters.searchQuery.trim()) params.set('q', filters.searchQuery.trim());
  if (filters.groupByOption) params.set('groupByOption', '1');
  if (filters.groupByChurch) params.set('groupByChurch', '1');
  const query = params.toString();
  return `/admin/events/${encodeURIComponent(eventId)}/applications/print${query ? `?${query}` : ''}`;
}

export function parseApplicationsPrintSearchParams(
  searchParams: URLSearchParams
): ApplicationsReportFilters {
  return {
    status: searchParams.get('status') || 'all',
    searchQuery: searchParams.get('q') || '',
    groupByOption: searchParams.get('groupByOption') === '1',
    groupByChurch: searchParams.get('groupByChurch') === '1',
  };
}

export function formatStatusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

export function formatApplicantName(app: ApplicationWithDetails): string {
  const profile = app.attendee_profile;
  const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ');
  return name || '—';
}

export function formatSubmittedAt(app: ApplicationWithDetails): string {
  if (!app.submitted_at) return '—';
  return new Date(app.submitted_at).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
